import { ImageIcon, Loader2, Video } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/app/routes';
import type { PostResponseDTO } from '@/api/generated/model';
import { postThumbnailSrc, formatCardDate } from '../lib/media';

/**
 * "Top Content" row — port of CMP `TopContentRow` / `TopContentCard` (StatisticsEngagementView.kt).
 * Square thumbnail cards with a date overlay (top-left) and image/video count (bottom-left); click
 * navigates to the content detail route (ROUTES.contentDetails), same as the "All Content" grid.
 */
function TopContentCard({ post }: { post: PostResponseDTO }) {
  const navigate = useNavigate();
  const src = postThumbnailSrc(post);
  const images = post.imageCount ?? 0;
  const videos = post.videoCount ?? 0;
  const count = images + videos;

  return (
    <button
      type="button"
      onClick={() => {
        if (post.id != null) void navigate(ROUTES.contentDetails(String(post.id)));
      }}
      className="group relative aspect-square w-32 shrink-0 overflow-hidden rounded-lg bg-muted text-left transition-opacity hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:w-36"
    >
      {src ? (
        <img src={src} alt="" className="size-full object-cover" loading="lazy" />
      ) : (
        <div className="flex size-full items-center justify-center text-muted-foreground">
          <ImageIcon className="size-6" />
        </div>
      )}
      {post.createdAt && (
        <span className="absolute left-1 top-1 rounded bg-black/50 px-1.5 py-0.5 text-[10px] text-white">
          {formatCardDate(post.createdAt)}
        </span>
      )}
      {count > 0 && (
        <div className="absolute bottom-1 left-1 flex items-center gap-1 text-white drop-shadow">
          {images > 0 && <ImageIcon className="size-4" />}
          {videos > 0 && <Video className="size-4" />}
          <span className="text-xs">{count}</span>
        </div>
      )}
    </button>
  );
}

export function TopContentGrid({
  posts,
  isLoading,
  isError,
}: {
  posts: PostResponseDTO[];
  isLoading: boolean;
  isError: boolean;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold">Top Content</h3>
        {/* Scrolls to the always-mounted "All Content" grid below (parity CMP "See all content"). */}
        <a href="#all-content" className="text-xs text-primary hover:underline">
          See all content
        </a>
      </div>
      {isLoading ? (
        <div className="flex h-36 items-center justify-center">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <p className="py-4 text-sm text-destructive">Failed to load top content.</p>
      ) : posts.length === 0 ? (
        <p className="py-4 text-sm text-muted-foreground">No content yet.</p>
      ) : (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {posts.map((p, i) => (
            <TopContentCard key={p.id ?? i} post={p} />
          ))}
        </div>
      )}
    </div>
  );
}
